// ResetPassword.jsx - Reset password with confirmation code
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FONT_SIZE, TOUCH } from '@/core/platform/platformStandards';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  LockReset as LockResetIcon,
  ArrowBack as BackIcon,
  Email as EmailIcon,
  VpnKey as KeyIcon
} from '@mui/icons-material';
import AuthLayout from '@/features/auth/components/AuthLayout';
import HFRLogo from '@/components/common/HFRLogo';
import PasswordResetModal from '@/features/auth/components/PasswordResetModal';
import authService from '@/services/authService';
import { useNotification } from '@/core/contexts/NotificationContext';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notifySuccess } = useNotification();
  const theme = useTheme();
  
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    confirmationCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const validateEmailAndCode = () => {
    const { email, confirmationCode } = formData;

    if (!email) {
      return 'Email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    if (!confirmationCode) {
      return 'Confirmation code is required';
    }

    if (confirmationCode.length < 6) {
      return 'Confirmation code must be at least 6 characters';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateEmailAndCode();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Open password modal for password entry
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (newPassword) => {
    setLoading(true);
    setError('');

    try {
      const result = await authService.resetPassword(
        formData.email,
        formData.confirmationCode,
        newPassword
      );
      
      if (result.success) {
        setShowPasswordModal(false);
        setSuccess(true);
        notifySuccess(result.message);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (error) setError(''); // Clear error when user types
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleBackToForgot = () => {
    navigate('/forgot-password');
  };

  return (
    <AuthLayout>
      <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
        {/* HFR Connect Logo */}
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            mb: 4
          }}
        >
          <HFRLogo width={140} height={70} />
        </Box>

        {!success ? (
          <>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Reset Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter the code from your email and choose a new password
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <TextField
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                fullWidth
                margin="dense"
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon
                        // intentional: adornment icon sized above theme default for visual balance with input text
                        sx={{ color: theme.palette.text.secondary, fontSize: FONT_SIZE.xl }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    minHeight: TOUCH.minSize,
                    fontSize: FONT_SIZE.sm,
                  },
                }}
              />

              <TextField
                label="Confirmation Code"
                value={formData.confirmationCode}
                onChange={handleInputChange('confirmationCode')}
                fullWidth
                margin="dense"
                autoComplete="one-time-code"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon
                        // intentional: adornment icon sized above theme default for visual balance with input text
                        sx={{ color: theme.palette.text.secondary, fontSize: FONT_SIZE.xl }}
                      />
                    </InputAdornment>
                  ),
                }}
                helperText="Enter the 6-digit code sent to your email"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    minHeight: TOUCH.minSize,
                    fontSize: FONT_SIZE.sm,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  mb: 3,
                  minHeight: { xs: TOUCH.minSize, sm: TOUCH.comfySize },
                  fontSize: FONT_SIZE.sm,
                }}
              >
                {loading ? (
                  <CircularProgress size={TOUCH.iconSize} color="inherit" />
                ) : (
                  'Continue to Set Password'
                )}
              </Button>
            </form>

            <Divider sx={{ my: 2 }} />

            {/* Navigation Buttons */}
            <Button
              onClick={handleBackToForgot}
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
            >
              Resend Code
            </Button>

            <Button
              onClick={handleBackToLogin}
              fullWidth
              variant="text"
              startIcon={<BackIcon />}
            >
              Back to Login
            </Button>
          </>
        ) : (
          <>
            {/* Success State */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <LockResetIcon 
                sx={{ 
                  // intentional: decorative illustration-size password reset success icon
                  fontSize: 40, 
                  color: 'success.main', 
                  mb: 2 
                }} 
              />
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Password Reset Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your password has been successfully updated. You can now login with your new password.
              </Typography>
            </Box>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>What&apos;s next:</strong>
                <br />
                • Your password has been changed
                <br />
                • You can now login with your new credentials
                <br />
                • Make sure to use a secure password manager
              </Typography>
            </Alert>

            {/* Action Button */}
            <Button
              onClick={handleBackToLogin}
              fullWidth
              variant="contained"
              size="large"
              sx={{ py: 1.5, minHeight: { xs: TOUCH.minSize, sm: TOUCH.comfySize } }}
            >
              Continue to Login
            </Button>
          </>
        )}
      </Box>

      {/* Password Reset Modal */}
      <PasswordResetModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordSubmit}
        loading={loading}
        error={error}
        title="Set New Password"
        submitButtonText="Reset Password"
      />
    </AuthLayout>
  );
};

export default ResetPassword;
